import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatError } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { switchMap, timer } from 'rxjs';
import { SelectOption } from '../../../../../models/local/select-option.model';
import { BaseSelectFormFieldComponent } from '../../../../form/field/base/base-form-field.component';
import { FormFieldErrorComponent } from '../../../../form/field/error/form-field-error.component';
import { FormFieldLabelComponent } from '../../../../form/field/label/form-field-label.component';
import { EntitySelectDataSource } from '../entity-select-data-source.model';
import { EntitySelectRowDirective } from '../select-row/entity-select-row.directive';
import { EntitySelectTriggerDirective } from '../select-trigger/entity-select-trigger.directive';
import { __ } from '../../../../../functions';

/**
 * A generic single-select searcher built on the `@angular/aria` combobox
 * dialog-popup pattern (https://angular.dev/guide/aria/combobox#dialog-popup).
 *
 * The trigger displays the currently selected option. Opening it shows a dialog popup
 * containing a search input and a listbox whose options are loaded from the `dataSource`.
 *
 * Like `<immo-entity-searcher>`, this generic component is not meant to be used at call
 * sites directly — build a per-entity wrapper extending `BaseEntitySelectComponent`,
 * which provides the `dataSource` from the entity service and the translated defaults.
 *
 * ```html
 * <immo-entity-select
 *     [field]="form().object"
 *     [source]="dataSource"
 *     [labelSelector]="labelSelector"
 *     [subLabelSelector]="subLabelSelector"
 *     panelLabel="Meine Objekte"
 * >
 *     <ng-template immoEntitySelectRow let-option>
 *         <immo-object-list-cell [entity]="option" />
 *     </ng-template>
 * </immo-entity-select>
 * ```
 */
@Component({
  selector: 'immo-entity-select',
  templateUrl: './entity-select.component.html',
  styleUrl: './entity-select.component.css',
  imports: [
    CommonModule,
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    Listbox,
    Option,
    OverlayModule,
    A11yModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FormFieldErrorComponent,
    FormFieldLabelComponent,
    MatError,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EntitySelectComponent<TOption = SelectOption> extends BaseSelectFormFieldComponent<
  TOption,
  TOption | null
> {
  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Whether the popup is currently open. Two-way bound to the `ngCombobox` trigger. */
  readonly expanded: WritableSignal<boolean> = signal(false);

  /** The current search query, written by the search input inside the popup. */
  readonly query: WritableSignal<string> = signal('');

  /**
   * Whether the popup was opened at least once — the options load LAZILY on first open,
   * so a select sitting in a toolbar doesn't fire a list request on every page load.
   */
  private readonly hasOpened: WritableSignal<boolean> = signal(false);

  /**
   * The listbox selection. Derived from the field value whenever it changes, matched
   * against the loaded options via `compareWith` so re-fetched instances still highlight.
   */
  readonly selectedValues: WritableSignal<TOption[]> = linkedSignal<TOption[]>(() => {
    const value = this.value();

    if (this.IsNullOrUndefined(value)) {
      return [];
    }

    const match = this.items().find((item) => this.compareWith()(item, value));

    return [this.IsNullOrUndefined(match) ? value : match];
  });

  /** Loads the options from the data source, debounced and cancelled per query change. */
  protected readonly itemsResource = rxResource({
    // undefined until the first open = no request — see `hasOpened`
    params: () =>
      this.hasOpened() ? { query: this.query().trim(), source: this.source() } : undefined,
    stream: ({ params }) =>
      timer(this.searchDebounceTime()).pipe(switchMap(() => params.source(params.query))),
  });

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The source the options are loaded from. Responsible for filtering by the query itself. */
  readonly source = input.required<EntitySelectDataSource<TOption>>();

  /** Maps an option to its main label. Falls back to the base `getLabel` (for `SelectOption`s). */
  readonly customLabelSelector = input<(option: TOption) => string>(undefined, {
    alias: 'labelSelector',
  });

  /** Maps an option to its sub label rendered below the main label in the default row. */
  readonly customSubLabelSelector = input<(option: TOption) => string>(undefined, {
    alias: 'subLabelSelector',
  });

  /** Visual variant, bound as `data-variant` on the trigger and panel for theming via a global theme.css. */
  readonly variant = input<string>('default');

  /** Optional heading above the search input, rendered as `PANEL LABEL · <count>`. */
  readonly panelLabel = input<string>('');

  /** Text shown when the data source returns no options. Pass a translated string. */
  readonly notFoundText = input<string>('');

  /** Debounce in milliseconds between typing and querying the data source. */
  readonly searchDebounceTime = input<number>(200);

  // -----------------------------------------------------------------------------------------------------
  // @ OUTPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly valueChanged = output<TOption>();

  // -----------------------------------------------------------------------------------------------------
  // @ CONTENT CHILDREN
  // -----------------------------------------------------------------------------------------------------

  readonly rowTemplate = contentChild(EntitySelectRowDirective, { read: TemplateRef<any> });

  readonly triggerTemplate = contentChild(EntitySelectTriggerDirective, { read: TemplateRef<any> });

  // -----------------------------------------------------------------------------------------------------
  // @ VIEW CHILDREN
  // -----------------------------------------------------------------------------------------------------

  protected readonly comboboxRef = viewChild(Combobox);

  protected readonly listboxRef = viewChild(Listbox);

  protected readonly listboxElement = viewChild('listboxElement', {
    read: ElementRef<HTMLElement>,
  });

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The loaded options for the current query. */
  readonly items = computed<TOption[]>(() =>
    this.itemsResource.hasValue() ? (this.itemsResource.value() ?? []) : [],
  );

  readonly isLoading = computed<boolean>(() => this.itemsResource.isLoading());

  /** The label of the currently selected option, shown on the trigger. */
  readonly triggerLabel = computed<string>(() => {
    const value = this.value();

    if (this.IsNullOrUndefined(value)) {
      return '';
    }

    return this.getOptionLabel(value);
  });

  // -----------------------------------------------------------------------------------------------------
  // @ EFFECTS
  // -----------------------------------------------------------------------------------------------------

  /** Resets the search whenever the popup opens so the full option list is shown again. */
  protected readonly resetQueryOnOpenEffect = effect(() => {
    if (this.expanded()) {
      this.hasOpened.set(true);
      this.query.set('');
    }
  });

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor() {
    super();

    afterRenderEffect(() => {
      if (this.comboboxRef()?.expanded() === true) {
        this.listboxRef()?.scrollActiveItemIntoView();
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PROTECTED METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resolves the main label of an option via the `labelSelector` input,
   * falling back to the base `getLabel` helper.
   *
   * @param option - The option to resolve the label for.
   * @returns {string} The label of the option, or an empty string for nullish options.
   */
  protected getOptionLabel(option: TOption | undefined): string {
    if (this.IsNullOrUndefined(option)) {
      return '';
    }

    const selector = this.customLabelSelector();

    if (!this.IsNullOrUndefined(selector)) {
      return selector(option);
    }

    return this.getLabel(option);
  }

  /**
   * Resolves the sub label of an option via the `subLabelSelector` input.
   *
   * @param option - The option to resolve the sub label for.
   * @returns {string} The sub label of the option, or an empty string when none is configured.
   */
  protected getOptionSubLabel(option: TOption): string {
    const selector = this.customSubLabelSelector();

    if (this.IsNullOrUndefined(option) || this.IsNullOrUndefined(selector)) {
      return '';
    }

    return selector(option);
  }

  /**
   * Builds the DOM id of the option at the given index, used to resolve the
   * active descendant back to an option for the row template context.
   *
   * @param index - The index of the option within the current items.
   * @returns {string} The DOM id assigned to the option element.
   */
  protected getOptionDomId(index: number): string {
    return `${this.fieldId()}-option-${index}`;
  }

  /**
   * Determines whether the given option is the currently selected one,
   * compared via `compareWith` so re-fetched instances still match.
   *
   * @param option - The option to check.
   * @returns {boolean} Returns true if the option is selected, false otherwise.
   */
  protected isSelected(option: TOption): boolean {
    return this.selectedValues().some((item) => this.compareWith()(item, option));
  }

  /**
   * Determines whether the option at the given index is the active
   * (keyboard-highlighted) descendant of the listbox.
   *
   * @param index - The index of the option within the current items.
   * @returns {boolean} Returns true if the option is the active descendant, false otherwise.
   */
  protected isActive(index: number): boolean {
    const listbox = this.listboxRef();

    if (this.IsNullOrUndefined(listbox) || !this.IsFunction(listbox.activeDescendant)) {
      return false;
    }

    return listbox.activeDescendant() === this.getOptionDomId(index);
  }

  /**
   * Toggles the popup. Bound to the trigger click explicitly, matching the
   * documented combobox examples which do not rely on implicit click handling.
   *
   * @returns {void}
   */
  protected toggle(): void {
    if (this.disabled()) {
      return;
    }

    this.expanded.set(!this.expanded());
  }

  /**
   * Commits the current listbox selection into the field, emits `valueChanged`,
   * and closes the popup while restoring focus to the trigger.
   *
   * @returns {void}
   */
  protected commit(): void {
    const selected = this.selectedValues();

    if (this.IsNullOrUndefinedOrEmpty(selected)) {
      return;
    }

    this.state().value.set(selected[0]);
    this.state().markAsTouched();
    this.valueChanged.emit(selected[0]);

    this.close();
  }

  /**
   * Closes the popup.
   *
   * @param refocusTrigger - Whether focus should be restored to the trigger element.
   * @returns {void}
   */
  protected close(refocusTrigger: boolean = true): void {
    this.expanded.set(false);

    if (refocusTrigger) {
      this.comboboxRef()?.element.focus();
    }
  }

  /**
   * Moves focus from the search input into the listbox (active-descendant navigation).
   *
   * @param event - The originating keyboard event, prevented so the page does not scroll.
   * @returns {void}
   */
  protected focusListbox(event: Event): void {
    event.preventDefault();

    this.listboxElement()?.nativeElement.focus();
  }

  /**
   * Handles keyboard input on the popup dialog: `Escape` closes it and
   * restores focus to the trigger, mirroring the documented dialog-popup pattern.
   *
   * @param event - The keyboard event raised inside the popup.
   * @returns {void}
   */
  protected onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.close();
  }
}
